import { useCallback, useEffect, useState } from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";
import { PaymentInfoCell } from "../../../../components/Admin/PaymentHistory/PaymentCell";
import { formatCurrency } from "../../../../hooks/formatNumber";
import { AdminAllPaymentHistory } from "../../../../types/adminPaymentHistory";
import LoadingOverlay from "../../../Shared/LoadingOverlay";

const rowsPerPage = 10;

type PaymentRecordsTableType = {
    records: AdminAllPaymentHistory[];
    setRecords: (v: AdminAllPaymentHistory[]) => void;
    queryRecords: () => Promise<AdminAllPaymentHistory[]>;
}

const PaymentRecordsTable = ({ records, setRecords, queryRecords }: PaymentRecordsTableType) => {
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const pageData = useCallback(() => {
        return records.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [page, records]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const response = await queryRecords();
        setIsLoading(false);
        setRecords(response);
    }, [setRecords, queryRecords]);

    useEffect(() => {
        if (records.length === 0) { 
            fetchData();
        }
        // eslint-disable-next-line
    }, []);

    const handlePageChange = async (_event: any, newPage: number) => {
        setPage(newPage);
    };
    
    return (
        <>
            <LoadingOverlay open={isLoading} />
            <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
                <Table sx={{ minWidth: 1100 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body2">Business Name</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Amount</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Tax</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Status</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">Email</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2">Record Count</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.length === 0 ? (
                            <TableRow key={0} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell component="th" scope="row">
                                    <Typography>No payments have been made</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageData().map((payment, index) =>
                                <TableRow
                                    key={index}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <PaymentInfoCell uid={payment.uid} name={payment.businessName} />
                                    <TableCell>
                                        <Typography>
                                            {formatCurrency(payment.amount / 100, "CAD", 'auto')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>
                                            {formatCurrency(payment.tax / 100, "CAD", 'auto')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography textTransform='capitalize'>
                                            {payment.status}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>{payment.email}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography>{payment.recordCount}</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={records.length}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                page={page}
                onPageChange={handlePageChange}
            />
        </>
    )
}

export default PaymentRecordsTable;
